import React from "react";
import GlassCard from "@/components/common/GlassCard";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function SEORobotsManager({ data, onSave }) {
  const [groups, setGroups] = React.useState(data?.groups || [{ user_agent: "*", rules: [{ type: "allow", path: "/" }] }]);
  const [sitemaps, setSitemaps] = React.useState((data?.sitemaps || []).join("\n"));
  const [raw, setRaw] = React.useState(data?.raw || "");
  const [mode, setMode] = React.useState("visual");
  React.useEffect(() => {
    setGroups(data?.groups?.length ? data.groups : [{ user_agent: "*", rules: [{ type: "allow", path: "/" }] }]);
    setSitemaps((data?.sitemaps || []).join("\n"));
    setRaw(data?.raw || "");
  }, [data]);
  const setGroup = (i, next) => setGroups((g) => g.map((row, idx) => (idx === i ? next : row)));
  return (
    <GlassCard className="p-5 space-y-3">
      <p className="text-sm text-white/60">Visual groups compile to robots.txt. Validate before publishing.</p>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" className={mode === "visual" ? "text-white" : "text-white/50"} onClick={() => setMode("visual")}>Visual</Button>
        <Button size="sm" variant="ghost" className={mode === "raw" ? "text-white" : "text-white/50"} onClick={() => setMode("raw")}>Raw</Button>
      </div>
      {mode === "visual" && (
        <div className="space-y-4">
          {groups.map((g, i) => (
            <div key={i} className="rounded-lg border border-white/10 p-3 space-y-2">
              <div className="flex gap-2">
                <Input value={g.user_agent} onChange={(e) => setGroup(i, { ...g, user_agent: e.target.value })} className="bg-black/20 border-white/10 text-white" placeholder="User-agent" />
                <Button variant="ghost" className="text-white/50" onClick={() => setGroups((rows) => rows.filter((_, idx) => idx !== i))}>Remove</Button>
              </div>
              {(g.rules || []).map((r, ri) => (
                <div key={ri} className="grid grid-cols-[120px_1fr_auto] gap-2">
                  <select value={r.type} onChange={(e) => {
                    const rules = [...(g.rules || [])]; rules[ri] = { ...r, type: e.target.value }; setGroup(i, { ...g, rules });
                  }} className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2">
                    <option value="allow">Allow</option>
                    <option value="disallow">Disallow</option>
                  </select>
                  <Input value={r.path} onChange={(e) => {
                    const rules = [...(g.rules || [])]; rules[ri] = { ...r, path: e.target.value }; setGroup(i, { ...g, rules });
                  }} className="bg-black/20 border-white/10 text-white" placeholder="/" />
                  <Button variant="ghost" className="text-white/50" onClick={() => setGroup(i, { ...g, rules: g.rules.filter((_, idx) => idx !== ri) })}>x</Button>
                </div>
              ))}
              <Button variant="ghost" className="text-white/70" onClick={() => setGroup(i, { ...g, rules: [...(g.rules || []), { type: "disallow", path: "/" }] })}>Add rule</Button>
            </div>
          ))}
          <Button variant="ghost" className="text-white/70" onClick={() => setGroups((g) => [...g, { user_agent: "*", rules: [{ type: "allow", path: "/" }] }])}>Add user-agent</Button>
          <Input value={sitemaps} onChange={(e) => setSitemaps(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="Sitemap URLs, one per line" />
          <Button onClick={() => onSave({ groups, sitemaps: sitemaps.split(/\n/).map((s) => s.trim()).filter(Boolean) })} className="btn-gradient text-white border-0">Save robots.txt</Button>
        </div>
      )}
      {mode === "raw" && (
        <>
          <Textarea rows={12} value={raw} onChange={(e) => setRaw(e.target.value)} className="bg-black/40 border-white/10 text-white font-mono text-xs" />
          {(data?.issues || []).map((i) => <p key={i} className="text-xs text-amber-300">{i}</p>)}
          <Button onClick={() => onSave({ raw })} className="btn-gradient text-white border-0">Save robots.txt</Button>
        </>
      )}
    </GlassCard>
  );
}

export function SEOSitemapManager({ data, onToggle }) {
  return (
    <GlassCard className="p-5 space-y-3">
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="text-white/60">Included <span className="text-white">{data?.included ?? 0}</span></div>
        <div className="text-white/60">Excluded <span className="text-white">{data?.excluded ?? 0}</span></div>
        <div className="text-white/40 text-xs">{data?.last_generated}</div>
      </div>
      <div className="max-h-72 overflow-auto space-y-1">
        {(data?.pages || []).map((p) => (
          <label key={p.id} className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={p.include !== false} onChange={(e) => onToggle(p.id, e.target.checked)} />
            <span className="font-mono text-xs">{p.path}</span>
          </label>
        ))}
      </div>
    </GlassCard>
  );
}

export function SEOReports({ data, onGenerate }) {
  return (
    <GlassCard className="p-5 space-y-3">
      <p className="text-sm text-white/60">Site health {data?.site?.health ?? "—"} · {data?.pages?.length || 0} pages. Search Console metrics are omitted until connected.</p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => onGenerate("json")} className="btn-gradient text-white border-0">Generate JSON report</Button>
        <Button variant="ghost" className="text-white/70" onClick={() => onGenerate("csv")}>Download CSV</Button>
        <Button variant="ghost" className="text-white/70" onClick={() => onGenerate("pdf")}>Download PDF</Button>
      </div>
    </GlassCard>
  );
}

export function SEOSettingsPanel({ data, onSave, onCrawl, gsc, onConnectGsc, onDisconnectGsc, onSelectGscSite, onRefreshGsc, onCreateGscTask, onOpenGscPage, measurement, onRunCwv, onRebuild, onCheckAlerts }) {
  const [form, setForm] = React.useState(data || {});
  React.useEffect(() => setForm(data || {}), [data]);
  const biz = form.local_business || {};
  const sites = gsc?.sites || [];
  const hookEnabled = form.deploy_hook_enabled ?? ((data?.measurement?.deploy_hook || measurement?.deploy_hook)?.enabled !== false);
  return (
    <div className="space-y-4">
      <GlassCard className="p-5 space-y-3">
        <p className="text-xs text-white/50">Production origin — use pages.dev during client review; switch to https://www.casmediasolutions.com after approval.</p>
        <Input value={form.production_origin || ""} onChange={(e) => setForm({ ...form, production_origin: e.target.value })} className="bg-black/20 border-white/10 text-white" placeholder="https://cas-media-website.pages.dev" />
        <select value={form.audit_schedule || "weekly"} onChange={(e) => setForm({ ...form, audit_schedule: e.target.value })} className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2">
          <option value="manual">Manual</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
        </select>
        <p className="text-xs text-white/40">Last scheduled audit: {form.last_audit_at || "never"}</p>
        <p className="text-[11px] text-white/35">
          Schedule runs on the API host (hourly check while the backend is up). Use &quot;Run full crawl now&quot; for an immediate pass. URL Health is on-demand under SEO → URL Health.
        </p>
        <Input value={biz.name || ""} onChange={(e) => setForm({ ...form, local_business: { ...biz, name: e.target.value } })} className="bg-black/20 border-white/10 text-white" placeholder="Business name" />
        <Input value={biz.address || ""} onChange={(e) => setForm({ ...form, local_business: { ...biz, address: e.target.value } })} className="bg-black/20 border-white/10 text-white" placeholder="Address" />
        <Input value={biz.phone || ""} onChange={(e) => setForm({ ...form, local_business: { ...biz, phone: e.target.value } })} className="bg-black/20 border-white/10 text-white" placeholder="Phone" />
        <div className="flex gap-2">
          <Button onClick={() => onSave({ ...form, deploy_hook_enabled: hookEnabled })} className="btn-gradient text-white border-0">Save settings</Button>
          {onCrawl && <Button variant="ghost" className="text-white/70" onClick={onCrawl}>Run full crawl now</Button>}
        </div>
      </GlassCard>

      <GlassCard className="p-5 space-y-3">
        <h3 className="text-sm text-white">Phase 3 — measurement</h3>
        <p className="text-xs text-white/45">
          Core Web Vitals use Google PageSpeed Insights (CrUX field data when available). Deploy hook rebuilds Cloudflare Pages after publish.
        </p>
        <ul className="text-xs text-white/60 space-y-2">
          <li className={(measurement?.pagespeed_configured || data?.measurement?.pagespeed_configured) ? "text-emerald-300/90" : "text-amber-200/80"}>
            {(measurement?.pagespeed_configured || data?.measurement?.pagespeed_configured) ? "✓" : "○"}{" "}
            <code className="text-white/80">PAGESPEED_API_KEY</code> on backend (PageSpeed Insights API)
          </li>
          <li className={(measurement?.deploy_hook?.configured || data?.measurement?.deploy_hook?.configured) ? "text-emerald-300/90" : "text-amber-200/80"}>
            {(measurement?.deploy_hook?.configured || data?.measurement?.deploy_hook?.configured) ? "✓" : "○"}{" "}
            <code className="text-white/80">CLOUDFLARE_PAGES_DEPLOY_HOOK_URL</code> (Pages → Settings → Deploy hooks)
          </li>
        </ul>
        <label className="flex items-center gap-2 text-sm text-white/70">
          <input
            type="checkbox"
            checked={hookEnabled}
            onChange={(e) => setForm({ ...form, deploy_hook_enabled: e.target.checked })}
          />
          Trigger site rebuild on SEO publish
        </label>
        {(measurement?.deploy_hook?.last_result || data?.measurement?.deploy_hook?.last_result) && (
          <p className="text-[11px] text-white/40">
            Last rebuild: {JSON.stringify(measurement?.deploy_hook?.last_result || data?.measurement?.deploy_hook?.last_result)}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {onRunCwv && <Button variant="ghost" className="text-white/70" onClick={onRunCwv}>Run CWV check</Button>}
          {onRebuild && <Button variant="ghost" className="text-white/70" onClick={onRebuild}>Rebuild site now</Button>}
          {onCheckAlerts && <Button variant="ghost" className="text-white/70" onClick={onCheckAlerts}>Check traffic alerts</Button>}
        </div>
      </GlassCard>

      <GlassCard className="p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm text-white">Google Search Console</h3>
            <p className="text-xs text-white/45 mt-1">
              {gsc?.connected
                ? `Connected${gsc.email ? ` as ${gsc.email}` : ""}${gsc.site_url ? ` · ${gsc.site_url}` : ""}`
                : (gsc?.note || "Not connected")}
            </p>
          </div>
          <div className="flex gap-2">
            {!gsc?.connected ? (
              <Button onClick={onConnectGsc} className="btn-gradient text-white border-0" disabled={!onConnectGsc}>
                Connect Google
              </Button>
            ) : (
              <>
                <Button variant="ghost" className="text-white/70" onClick={onRefreshGsc}>Refresh data</Button>
                <Button variant="ghost" className="text-red-300/80" onClick={onDisconnectGsc}>Disconnect</Button>
              </>
            )}
          </div>
        </div>
        {gsc?.connected && (
          <div className="space-y-2">
            <label className="text-[11px] uppercase tracking-wider text-white/40">Property</label>
            <select
              value={gsc.site_url || ""}
              onChange={(e) => onSelectGscSite?.(e.target.value)}
              className="w-full h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2"
            >
              <option value="">Select a verified property…</option>
              {sites.map((s) => (
                <option key={s.site_url} value={s.site_url}>{s.site_url} ({s.permission || "access"})</option>
              ))}
            </select>
            {sites.length === 0 && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100/90 space-y-2">
                <p>
                  Google account is connected, but <strong>no Search Console properties</strong> were returned for{" "}
                  {gsc.email || "this account"}.
                </p>
                <p className="text-amber-100/70">
                  Add and verify your site (URL-prefix or Domain) in Search Console using this same Google account, then click Refresh data.
                </p>
                <a
                  className="inline-flex text-cyan-300 hover:underline"
                  href="https://search.google.com/search-console"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Google Search Console →
                </a>
              </div>
            )}
          </div>
        )}
        <SEOSearchConsole data={gsc} compact onCreateTask={onCreateGscTask} onOpenPage={onOpenGscPage} />
      </GlassCard>

      <GlassCard className="p-5 space-y-3">
        <h3 className="text-sm text-white">Phase 0 — stabilize</h3>
        <ul className="text-xs text-white/60 space-y-2">
          <li className={gsc?.connected ? "text-emerald-300/90" : "text-white/50"}>
            {gsc?.connected ? "✓" : "○"} GSC OAuth connected{gsc?.email ? ` (${gsc.email})` : ""}
          </li>
          <li className={gsc?.site_url ? "text-emerald-300/90" : "text-amber-200/80"}>
            {gsc?.site_url ? `✓ Property selected (${gsc.site_url})` : "○ Property selected — add a verified site in Search Console first"}
          </li>
          <li className="text-emerald-300/90">✓ Refresh-token login ready — log out and log in once on this browser if you have not since the auth update</li>
          <li className="text-amber-200/80">
            ○ Rotate Gemini API key in{" "}
            <a className="text-cyan-300 hover:underline" href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer">
              AI Studio
            </a>{" "}
            (key was exposed in chat), then paste the new value into backend <code className="text-white/80">.env</code> as <code className="text-white/80">GEMINI_API_KEY</code> and restart API
          </li>
          <li className="text-amber-200/80">
            ○ Disable <strong>Poper Blocker</strong> for localhost (chrome://extensions) or use Incognito without extensions — it breaks admin after Google redirects
          </li>
        </ul>
      </GlassCard>
    </div>
  );
}

export function SEOIssuesTasks({ issues, tasks, onIssue, onTask, onCompleteTask, onOpenPage }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <GlassCard className="p-5">
        <h3 className="text-sm text-white mb-3">Open issues</h3>
        {(issues || []).map((i) => (
          <div key={i.id} className="flex gap-2 text-sm text-white/70 py-2 border-b border-white/5 items-start">
            <button type="button" className="flex-1 text-left hover:text-white" onClick={() => onOpenPage?.(i.page_id)}>{i.message}</button>
            <button className="text-emerald-300/80 shrink-0" onClick={() => onIssue(i.id, "resolved")}>Mark resolved</button>
            <button className="text-white/40 shrink-0" onClick={() => onIssue(i.id, "ignored")}>Ignore</button>
            <button className="text-white/40 shrink-0" onClick={() => onTask(i)}>Task</button>
          </div>
        ))}
        {(issues || []).length === 0 && <p className="text-white/40 text-sm">No open issues.</p>}
      </GlassCard>
      <GlassCard className="p-5">
        <h3 className="text-sm text-white mb-3">SEO tasks</h3>
        {(tasks || []).map((t) => (
          <div key={t.id} className="flex gap-2 text-sm text-white/70 py-2 border-b border-white/5">
            <span className="flex-1">{t.title} · {t.status}</span>
            {t.page_id && <button className="text-cyan-300/80" onClick={() => onOpenPage?.(t.page_id)}>Open</button>}
            {t.status !== "done" && t.status !== "resolved" && onCompleteTask && <button className="text-white/40" onClick={() => onCompleteTask(t.id)}>Done</button>}
          </div>
        ))}
        {(tasks || []).length === 0 && <p className="text-white/40 text-sm">No tasks yet.</p>}
      </GlassCard>
    </div>
  );
}

export function SEOSiteFiles({ robots, sitemap, productionOrigin, onSaveRobots, onToggleSitemap, geo, onSaveGeo, onPingIndexNow }) {
  const apiBase = (typeof window !== "undefined" && (process.env.REACT_APP_BACKEND_URL || "")) || "https://cas-admin-backend.onrender.com";
  const siteOrigin = (productionOrigin || "https://cas-media-website.pages.dev").replace(/\/$/, "");
  const [llms, setLlms] = React.useState(geo?.llms_txt || "");
  const [indexEnabled, setIndexEnabled] = React.useState(geo?.indexnow?.enabled !== false);
  React.useEffect(() => {
    setLlms(geo?.llms_txt || "");
    setIndexEnabled(geo?.indexnow?.enabled !== false);
  }, [geo]);
  return (
    <div className="space-y-4">
      <GlassCard className="p-4 text-sm text-white/60">
        Live site files (via Pages Functions, ~60s cache):{" "}
        <a className="text-cyan-300" href={`${siteOrigin}/robots.txt`} target="_blank" rel="noreferrer">/robots.txt</a>
        {" · "}
        <a className="text-cyan-300" href={`${siteOrigin}/sitemap.xml`} target="_blank" rel="noreferrer">/sitemap.xml</a>
        {" · "}
        <a className="text-cyan-300" href={`${siteOrigin}/llms.txt`} target="_blank" rel="noreferrer">/llms.txt</a>
        {" · "}
        <a className="text-cyan-300" href={`${siteOrigin}/indexnow-key.txt`} target="_blank" rel="noreferrer">/indexnow-key.txt</a>
        <p className="text-[11px] text-amber-200/70 mt-2">Page title/canonical/OG in view-source update only after a website rebuild &amp; deploy. Robots, sitemap, llms.txt, and IndexNow key are live without rebuild.</p>
      </GlassCard>
      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm text-white mb-2">Robots.txt</h3>
          <SEORobotsManager data={robots} onSave={onSaveRobots} />
        </div>
        <div>
          <h3 className="text-sm text-white mb-2">Sitemap includes</h3>
          <SEOSitemapManager data={sitemap} onToggle={onToggleSitemap} />
        </div>
      </div>
      <GlassCard className="p-5 space-y-3">
        <h3 className="text-sm text-white">GEO — llms.txt</h3>
        <p className="text-xs text-white/45">Briefing for AI crawlers (citation-friendly). Served at /llms.txt.</p>
        <textarea
          value={llms}
          onChange={(e) => setLlms(e.target.value)}
          rows={12}
          className="w-full rounded-md bg-black/20 border border-white/10 text-white text-xs p-3 font-mono"
        />
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => onSaveGeo?.({ llms_txt: llms, indexnow_enabled: indexEnabled })}
            className="btn-gradient text-white border-0"
          >
            Save GEO files
          </Button>
        </div>
      </GlassCard>
      <GlassCard className="p-5 space-y-3">
        <h3 className="text-sm text-white">IndexNow</h3>
        <p className="text-xs text-white/45">
          On publish, URLs are submitted to IndexNow (Bing &amp; compatible engines). Key file:{" "}
          <span className="text-white/70">{geo?.indexnow?.key_location || `${siteOrigin}/indexnow-key.txt`}</span>
        </p>
        <label className="flex items-center gap-2 text-sm text-white/70">
          <input type="checkbox" checked={indexEnabled} onChange={(e) => setIndexEnabled(e.target.checked)} />
          Enabled on publish
        </label>
        {geo?.indexnow?.last_result && (
          <p className="text-[11px] text-white/40">
            Last ping: {geo.indexnow.last_result.ok ? "ok" : "failed"} · status {geo.indexnow.last_result.status ?? "—"} ·{" "}
            {geo.indexnow.last_url || "—"}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            className="text-white/70"
            onClick={() => onSaveGeo?.({ llms_txt: llms, indexnow_enabled: indexEnabled })}
          >
            Save IndexNow toggle
          </Button>
          <Button variant="ghost" className="text-cyan-300/90" onClick={() => onPingIndexNow?.(["/"])}>
            Ping homepage now
          </Button>
          <Button
            variant="ghost"
            className="text-amber-200/80"
            onClick={() => onSaveGeo?.({ llms_txt: llms, indexnow_enabled: indexEnabled, regenerate_indexnow_key: true })}
          >
            Rotate IndexNow key
          </Button>
        </div>
      </GlassCard>
    </div>
  );
}

export function SEOUrlHealth({ data, performance, onRunCwv }) {
  if (!data) return <GlassCard className="p-8 text-white/40">Loading URL health…</GlassCard>;
  const cwv = performance?.cwv || {};
  return (
    <div className="space-y-4">
      <GlassCard className="p-5 grid md:grid-cols-3 gap-3 text-sm text-white/70">
        <div>Pages {data.page_count}</div>
        <div>HTTPS {data.https_ok ? "OK" : "Issues"}</div>
        <div>Duplicate paths {(data.duplicates || []).length}</div>
      </GlassCard>

      <GlassCard className="p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm text-white">Core Web Vitals</h3>
            <p className="text-xs text-white/45 mt-1">CrUX field data via PageSpeed Insights (not crawl TTFB).</p>
          </div>
          {onRunCwv && <Button size="sm" className="btn-gradient text-white border-0" onClick={onRunCwv}>Run CWV check</Button>}
        </div>
        {cwv.ok ? (
          <div className="grid sm:grid-cols-4 gap-3 text-sm text-white/75">
            <div><span className="text-white/40 text-xs block">LCP</span>{cwv.lcp_ms ?? "—"} ms</div>
            <div><span className="text-white/40 text-xs block">CLS</span>{cwv.cls ?? "—"}</div>
            <div><span className="text-white/40 text-xs block">INP</span>{cwv.inp_ms ?? "—"} ms</div>
            <div><span className="text-white/40 text-xs block">Source</span>{cwv.source} · score {cwv.performance_score ?? "—"}</div>
          </div>
        ) : (
          <p className="text-sm text-white/50">
            {performance?.pagespeed_configured
              ? "No CWV snapshot yet — run a check for the homepage."
              : "Add PAGESPEED_API_KEY to the backend env (Google Cloud → PageSpeed Insights API), restart API, then run a check."}
          </p>
        )}
        {performance?.avg_ms != null && (
          <p className="text-[11px] text-white/40">Crawl TTFB average: {performance.avg_ms} ms (separate from CWV).</p>
        )}
      </GlassCard>

      <SEOPerformance data={performance} />

      {(data.duplicates || []).map((d) => <p key={d.path} className="text-xs text-amber-300">Duplicate {d.path} × {d.count}</p>)}
      {(data.redirect_chains || []).map((c) => <p key={c.from} className="text-xs text-amber-300">Chain {c.from} → {c.to} ({c.hops} hops)</p>)}
      {(data.issues || []).map((i, idx) => <p key={idx} className="text-xs text-white/60">{i.type}: {i.message}</p>)}
      {!(data.issues || []).length && !(data.duplicates || []).length && <p className="text-sm text-white/50">No structural URL issues in the registry.</p>}
    </div>
  );
}

export function SEOSearchConsole({ data, compact, onCreateTask, onOpenPage }) {
  const queries = data?.queries || [];
  return (
    <GlassCard className={compact ? "p-4 space-y-3 border-white/5 bg-black/10" : "p-6 space-y-3"}>
      {!compact && (
        <>
          <p className="text-white">{data?.connected ? "Connected" : "Not connected"}</p>
          <p className="text-sm text-white/60">{data?.note}</p>
        </>
      )}
      {compact && data?.note && !data?.connected && (
        <p className="text-sm text-white/50">{data.note}</p>
      )}
      {data?.error && data?.note && (
        <p className="text-xs text-amber-200/80">{data.note}</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase text-white/40">
            <tr>
              <th className="text-left py-2">Query</th>
              <th className="text-right">Clicks</th>
              <th className="text-right">Impressions</th>
              <th className="text-right">CTR %</th>
              <th className="text-right">Position</th>
              {onCreateTask && <th className="text-right">Fix</th>}
            </tr>
          </thead>
          <tbody>
            {queries.length === 0 && (
              <tr>
                <td colSpan={onCreateTask ? 6 : 5} className="py-8 text-center text-white/40">
                  {data?.connected
                    ? (data?.site_url ? "No query rows for the selected range yet." : "Select a property above to load rankings.")
                    : "Rankings stay empty until Search Console is authorized."}
                </td>
              </tr>
            )}
            {queries.map((q) => (
              <tr key={q.query} className="border-t border-white/5 text-white/75">
                <td className="py-2 pr-3">{q.query}</td>
                <td className="text-right">{q.clicks}</td>
                <td className="text-right">{q.impressions}</td>
                <td className="text-right">{q.ctr}</td>
                <td className="text-right">{q.position}</td>
                {onCreateTask && (
                  <td className="text-right">
                    <button type="button" className="text-cyan-300/90 text-xs hover:underline" onClick={() => onCreateTask({ kind: "query", ...q })}>
                      Task
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(data?.pages || []).length > 0 && (
        <div className="overflow-x-auto pt-2">
          <p className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Top pages</p>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase text-white/40">
              <tr>
                <th className="text-left py-2">Page</th>
                <th className="text-right">Clicks</th>
                <th className="text-right">Impressions</th>
                <th className="text-right">Position</th>
                {(onCreateTask || onOpenPage) && <th className="text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {data.pages.slice(0, 10).map((p) => (
                <tr key={p.page} className="border-t border-white/5 text-white/70">
                  <td className="py-2 pr-3 truncate max-w-[280px]">{p.page}</td>
                  <td className="text-right">{p.clicks}</td>
                  <td className="text-right">{p.impressions}</td>
                  <td className="text-right">{p.position}</td>
                  {(onCreateTask || onOpenPage) && (
                    <td className="text-right space-x-2 whitespace-nowrap">
                      {onOpenPage && (
                        <button type="button" className="text-cyan-300/90 text-xs hover:underline" onClick={() => onOpenPage(p)}>
                          Open
                        </button>
                      )}
                      {onCreateTask && (
                        <button type="button" className="text-cyan-300/90 text-xs hover:underline" onClick={() => onCreateTask({ kind: "page", ...p })}>
                          Task
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </GlassCard>
  );
}

export function SEOPerformance({ data }) {
  return (
    <GlassCard className="p-5 space-y-3">
      <p className="text-sm text-white/60">Crawl response times (TTFB from last analyze — separate from Core Web Vitals above).</p>
      <p className="text-sm text-white/70">Average response {data?.avg_ms ?? "—"} ms</p>
      {(data?.slowest || []).map((r) => <div key={r.path} className="text-sm text-white/70 flex justify-between"><span className="font-mono text-xs">{r.path}</span><span>{r.ms} ms · {r.status}</span></div>)}
      {!(data?.slowest || []).length && <p className="text-white/40 text-sm">Analyze pages to collect timings.</p>}
    </GlassCard>
  );
}

export function SEOCrawlHistory({ items }) {
  return (
    <GlassCard className="p-5 space-y-2">
      {(items || []).map((j) => (
        <div key={j.id} className="text-sm text-white/70 border-b border-white/5 py-2 flex justify-between">
          <span>{j.type || "crawl"} · {j.status}</span>
          <span className="text-white/40">{j.done}/{j.total} · {(j.created_at || "").slice(0, 16)}</span>
        </div>
      ))}
      {!(items || []).length && <p className="text-white/40 text-sm">No crawl jobs yet.</p>}
    </GlassCard>
  );
}

export function SEOSchemaStudio({ templates, page, onChange, onValidate }) {
  const sample = templates?.sample || {};
  const names = templates?.templates || Object.keys(sample);
  const [json, setJson] = React.useState(typeof page?.schema === "object" ? (page.schema.configured || "") : "");
  const [result, setResult] = React.useState(null);
  React.useEffect(() => {
    setJson(typeof page?.schema === "object" ? (page.schema.configured || "") : (page?.schema || ""));
  }, [page]);
  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-4">
      <GlassCard className="p-4 space-y-1">
        {names.map((n) => (
          <button key={n} className="block w-full text-left text-sm text-white/70 hover:text-white py-1" onClick={() => setJson(JSON.stringify(sample[n] || { "@type": n }, null, 2))}>{n}</button>
        ))}
      </GlassCard>
      <GlassCard className="p-5 space-y-3">
        <Textarea rows={16} value={json} onChange={(e) => setJson(e.target.value)} className="bg-black/40 border-white/10 text-white font-mono text-xs" />
        <div className="flex gap-2">
          <Button variant="ghost" className="text-white/70" onClick={async () => setResult(await onValidate(json))}>Validate</Button>
          {page && onChange && <Button className="btn-gradient text-white border-0" onClick={() => onChange({ ...page, schema: { ...(page.schema || {}), configured: json } })}>Apply to current page</Button>}
        </div>
        {result && <p className={`text-sm ${result.ok ? "text-emerald-300" : "text-amber-300"}`}>{result.ok ? "Valid JSON-LD" : (result.errors || []).join(" ")}</p>}
        {!page && <p className="text-xs text-white/40">Open a page from SEO Pages to apply schema as a draft.</p>}
      </GlassCard>
    </div>
  );
}
